import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type {
  ChangePasswordSchema,
  ForceChangePasswordSchema,
  SignInSchema,
  UpdateProfileSchema,
} from '@/api/auth/auth.schema';
import type { User } from '@/db/schema';
import { useRouter } from '@/hooks/use-router';
import { useToast } from '@/hooks/use-toast';
import { apiClient, unwrap } from '@/lib/api-client';
import { ac, can } from '@/lib/permissions';
import type { ApiError } from '@/types/api/error-response.type';

export const authApi = {
  signIn: async (data: SignInSchema) => {
    const res = await apiClient.auth['sign-in'].post(data);
    return unwrap(res.data);
  },
  signOut: async () => {
    const res = await apiClient.auth['sign-out'].post();
    return unwrap(res.data);
  },

  getCurrentUser: async () => {
    try {
      const res = await apiClient.auth.me.get();
      return unwrap(res.data);
    } catch {
      return null;
    }
  },

  updateProfile: async (data: UpdateProfileSchema) => {
    const res = await apiClient.auth.me.put(data);
    return unwrap(res.data);
  },

  changePassword: async (data: ChangePasswordSchema) => {
    const res = await apiClient.auth['change-password'].put(data);
    return unwrap(res.data);
  },

  forceChangePassword: async (data: ForceChangePasswordSchema) => {
    const res = await apiClient.auth['force-change-password'].put(data);
    return unwrap(res.data);
  },
};

export const authKey = 'auth';

export const authKeys = {
  all: [authKey] as const,
  user: () => [...authKeys.all, 'user'] as const,
};

export const useSignIn = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const router = useRouter();

  return useMutation<Awaited<ReturnType<typeof authApi.signIn>>, ApiError, SignInSchema>({
    mutationFn: authApi.signIn,
    onSuccess: async ({ user }) => {
      queryClient.setQueryData(authKeys.user(), user);
      toast.success('Login realizado com sucesso!');
      setTimeout(() => {
        router.refresh();
      }, 400);
    },
    onError: (e) => {
      toast.error(e.message);
    },
  });
};

export const useSignOut = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const router = useRouter();

  return useMutation({
    mutationFn: authApi.signOut,
    onSuccess: async () => {
      queryClient.removeQueries();
      toast.success('Logout realizado com sucesso!');
      router.refresh();
    },
  });
};

export const useCurrentUser = () => {
  return useSuspenseQuery({
    queryKey: authKeys.user(),
    queryFn: authApi.getCurrentUser,
    staleTime: Infinity,
    retry: false,
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<
    Awaited<ReturnType<typeof authApi.updateProfile>>,
    ApiError,
    UpdateProfileSchema
  >({
    mutationFn: authApi.updateProfile,
    onSuccess: async (data) => {
      if (data.status) {
        queryClient.invalidateQueries({ queryKey: authKeys.user() });
        toast.success('Perfil atualizado com sucesso!');
      }
    },
    onError: (e) => {
      toast.error(e.message);
    },
  });
};

export const useChangePassword = () => {
  const toast = useToast();

  return useMutation<
    Awaited<ReturnType<typeof authApi.changePassword>>,
    ApiError,
    ChangePasswordSchema
  >({
    mutationFn: authApi.changePassword,
    onSuccess: () => {
      toast.success('Senha alterada com sucesso!');
    },
    onError: (e) => {
      toast.error(e.message);
    },
  });
};

export const useForceChangePassword = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<
    Awaited<ReturnType<typeof authApi.forceChangePassword>>,
    ApiError,
    ForceChangePasswordSchema
  >({
    mutationFn: authApi.forceChangePassword,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
      toast.success('Senha alterada com sucesso!');
    },
    onError: (e) => {
      toast.error(e.message);
    },
  });
};

export const useIsAuthenticated = () => {
  const { data, isLoading } = useCurrentUser();

  return {
    isAuthenticated: !!data?.user,
    isLoading,
    user: data?.user as User,
  };
};

type PermissionActions<R extends keyof typeof ac.statements> =
  (typeof ac.statements)[R][number];

type PermissionMap<R extends keyof typeof ac.statements> = {
  [K in PermissionActions<R> as `can${Capitalize<K>}`]: boolean;
};

export const usePermissions = () => {
  const { data } = useCurrentUser();

  const hasPermission = <R extends keyof typeof ac.statements>(
    resource: R,
    action: PermissionActions<R>,
  ) => {
    return can(data?.user.role, resource, action);
  };

  const createPermissions = <R extends keyof typeof ac.statements>(
    resource: R,
  ): PermissionMap<R> => {
    const actions = ac.statements[resource];

    return Object.fromEntries(
      actions.map((action) => [
        `can${action.charAt(0).toUpperCase()}${action.slice(1)}`,
        hasPermission(resource, action),
      ]),
    ) as PermissionMap<R>;
  };

  return {
    hasPermission,
    createPermissions,
  };
};
